import type {
  ImportedPieceView,
  RenamedImportedPiece,
} from '@obraplan/core/application/contracts/takeoff';
import type { Session } from '@obraplan/core/application/session';
import type { ProjectId, UnitTypeId } from '@obraplan/core/domain/shared/ids';
import type { Container } from '@/app/container';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * As peças da planilha na planta (Fase 28, etapa 3 — RN-427 a RN-430), do lado da página.
 *
 * A planta (`PlanTracer`) não conhece o container, como com a prancha: recebe a lista já lida e
 * os gestos — posicionar, tirar e renomear. Os três gravam **na hora**, fora do rascunho da
 * bancada: o ponto identifica a peça e não muda o quantitativo, e esperar o "Salvar" para ele
 * seria perdê-lo ao trocar de tipologia.
 *
 * O nome novo (20/10/2026, RN-457) é o único que a mesa também guarda — as quantidades e a peça
 * desenhada vão nela pelo nome. O servidor renomeia, e `aoRenomear` refaz o mesmo na mesa,
 * para a próxima gravação da bancada não levar o nome antigo de volta.
 */
export interface PecasDaPlanilhaDaMesa {
  readonly pecas: readonly ImportedPieceView[];
  /** Uma gravação em andamento: a planta espera antes do próximo clique. */
  readonly ocupado: boolean;
  readonly erro?: string | undefined;
  /** Põe — ou move — a peça no ponto, em metros nas coordenadas da tipologia. */
  readonly posicionar: (nome: string, ponto: { readonly x: number; readonly y: number }) => void;
  readonly tirar: (nome: string) => void;
  /** Dá à peça um nome novo; responde com ele quando gravou, para a tela fechar o campo. */
  readonly renomear: (nome: string, novo: string) => Promise<string | undefined>;
}

export type CasosDasPecasDaPlanilha = Pick<
  Container,
  | 'getImportedPieces'
  | 'setImportedPiecePosition'
  | 'clearImportedPiecePosition'
  | 'renameImportedPiece'
>;

export function usePecasDaPlanilha({
  container,
  session,
  projectId,
  unitTypeId,
  aoRenomear,
}: {
  container: CasosDasPecasDaPlanilha;
  session: Session | undefined;
  projectId: ProjectId | undefined;
  unitTypeId: UnitTypeId;
  /** O que o servidor renomeou, para a mesa refazer (`useWorkspaceDraft`). */
  aoRenomear?: (renomeada: RenamedImportedPiece) => void;
}): PecasDaPlanilhaDaMesa | undefined {
  const lido = useLive(
    () =>
      session === undefined || projectId === undefined
        ? Promise.resolve(undefined)
        : container.getImportedPieces.execute({ projectId, unitTypeId }, session),
    [session?.userId, projectId, unitTypeId],
  );

  const [posicionar, posicionarState] = useCommand(
    (nome: string, ponto: { readonly x: number; readonly y: number }) =>
      container.setImportedPiecePosition.execute(
        { projectId, unitTypeId, pieceName: nome, x: ponto.x, y: ponto.y },
        session!,
      ),
    { successMessage: (posicao) => `${posicao.pieceName} na planta.` },
  );
  const [tirar, tirarState] = useCommand(
    (nome: string) =>
      container.clearImportedPiecePosition.execute(
        { projectId, unitTypeId, pieceName: nome },
        session!,
      ),
    { successMessage: 'Peça tirada da planta — ela volta para a fila.' },
  );

  const [renomear, renomearState] = useCommand(
    (nome: string, novo: string) =>
      container.renameImportedPiece.execute(
        { projectId, unitTypeId, pieceName: nome, newName: novo },
        session!,
      ),
    {
      onSuccess: (renomeada) => aoRenomear?.(renomeada),
      successMessage: (renomeada) => `${renomeada.previousName} agora é ${renomeada.pieceName}.`,
    },
  );

  // A tipologia sem planilha — ou ainda não gravada no servidor — não tem lista nenhuma.
  if (lido === undefined || !lido.ok || lido.value.pieces.length === 0) return undefined;

  const erro = posicionarState.error ?? tirarState.error ?? renomearState.error;
  return {
    pecas: lido.value.pieces,
    ocupado: posicionarState.isPending || tirarState.isPending || renomearState.isPending,
    ...(erro === undefined ? {} : { erro: erro.message }),
    posicionar: (nome, ponto) => void posicionar(nome, ponto),
    tirar: (nome) => void tirar(nome),
    renomear: async (nome, novo) => {
      const feito = await renomear(nome, novo);
      return feito?.ok === true ? feito.value.pieceName : undefined;
    },
  };
}
